"use client";

import { useCallback, useRef, useState } from "react";
import Image from "next/image";
import { motion } from "framer-motion";

type Props = {
  name: string;
  title: string;
  beforeImage: string;
  afterImage: string;
  delay?: number;
};

export function BeforeAfterSlider({ name, title, beforeImage, afterImage, delay = 0 }: Props) {
  const [percent, setPercent] = useState(50);
  const [dragging, setDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const playedRef = useRef(false);

  const isDraggingRef = useRef(false);
  const isScrollingRef = useRef(false);
  const touchStartPos = useRef<{ x: number; y: number } | null>(null);

  const setFromClientX = useCallback((clientX: number) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPercent(Math.min(96, Math.max(4, pct)));
  }, []);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === "mouse") {
      if (e.button !== 0) return;
      setDragging(true);
      isDraggingRef.current = true;
      try {
        containerRef.current?.setPointerCapture(e.pointerId);
      } catch {}
      setFromClientX(e.clientX);
      return;
    }

    // Touch event: record starting point but do NOT capture pointer yet
    // to allow native vertical page scrolling (pan-y)
    touchStartPos.current = { x: e.clientX, y: e.clientY };
    isDraggingRef.current = false;
    isScrollingRef.current = false;
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === "mouse") {
      if (!isDraggingRef.current) return;
      setFromClientX(e.clientX);
      return;
    }

    // Touch event handling:
    if (!touchStartPos.current || isScrollingRef.current) return;

    if (isDraggingRef.current) {
      setFromClientX(e.clientX);
      return;
    }

    const deltaX = Math.abs(e.clientX - touchStartPos.current.x);
    const deltaY = Math.abs(e.clientY - touchStartPos.current.y);

    // Initial movement jitter threshold
    if (deltaX < 10 && deltaY < 10) return;

    // If movement is predominantly vertical, user is scrolling the page!
    if (deltaY >= 8 || deltaY >= deltaX) {
      isScrollingRef.current = true;
      return;
    }

    // Deliberate horizontal swipe detected: user wants to drag the comparison slider!
    if (deltaX >= 15 && deltaX > deltaY * 1.5) {
      isDraggingRef.current = true;
      setDragging(true);
      try {
        containerRef.current?.setPointerCapture(e.pointerId);
      } catch {}
      setFromClientX(e.clientX);
    }
  };

  const endDrag = (e?: React.PointerEvent<HTMLDivElement>) => {
    // Check if it was a quick stationary tap
    if (
      e &&
      e.pointerType === "touch" &&
      touchStartPos.current &&
      !isScrollingRef.current &&
      !isDraggingRef.current
    ) {
      const deltaX = Math.abs(e.clientX - touchStartPos.current.x);
      const deltaY = Math.abs(e.clientY - touchStartPos.current.y);
      if (deltaX < 8 && deltaY < 8) {
        setFromClientX(e.clientX);
      }
    }

    if (e && isDraggingRef.current && containerRef.current?.hasPointerCapture(e.pointerId)) {
      try {
        containerRef.current?.releasePointerCapture(e.pointerId);
      } catch {}
    }
    isDraggingRef.current = false;
    isScrollingRef.current = false;
    touchStartPos.current = null;
    setDragging(false);
  };

  const handleHandlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    // Direct touch or click on the slider handle thumb activates drag immediately
    e.stopPropagation();
    setDragging(true);
    isDraggingRef.current = true;
    isScrollingRef.current = false;
    touchStartPos.current = { x: e.clientX, y: e.clientY };
    try {
      containerRef.current?.setPointerCapture(e.pointerId);
    } catch {}
    setFromClientX(e.clientX);
  };

  const playSweep = () => {
    if (playedRef.current) return;
    playedRef.current = true;
    [22, 78, 50].forEach((value, i) => {
      setTimeout(() => {
        if (!isDraggingRef.current) {
          setPercent(value);
        }
      }, 350 + i * 750);
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      onViewportEnter={playSweep}
      className="flex flex-col items-center w-full"
    >
      <div
        ref={containerRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onLostPointerCapture={() => endDrag()}
        onDragStart={(e) => e.preventDefault()}
        className="relative aspect-[3/4] w-full max-w-[320px] sm:max-w-none touch-pan-y select-none overflow-hidden rounded-2xl border border-brand-gray-light bg-brand-gray-light shadow-sm transition-shadow duration-300 hover:shadow-xl cursor-ew-resize"
      >
        {/* After Image (Full Base) */}
        <div className="absolute inset-0">
          <Image
            src={afterImage}
            alt={`${name} — after hair transplant at Dermalife`}
            fill
            draggable={false}
            sizes="(max-width: 640px) 70vw, 20vw"
            className="pointer-events-none object-cover"
          />
          <span className="absolute right-3 top-3 z-10 rounded-full bg-white/70 px-2 py-0.5 font-accent text-[10px] uppercase text-brand-black backdrop-blur-sm">
            After
          </span>
        </div>

        {/* Before Image (Overlaid & Clipped) */}
        <div
          className="absolute inset-0 z-10"
          style={{
            clipPath: `inset(0 ${100 - percent}% 0 0)`,
            transition: dragging ? "none" : "clip-path 0.7s cubic-bezier(0.22,1,0.36,1)",
          }}
        >
          <Image
            src={beforeImage}
            alt={`${name} — before hair transplant`}
            fill
            draggable={false}
            sizes="(max-width: 640px) 70vw, 20vw"
            className="pointer-events-none object-cover"
          />
          <span className="absolute left-3 top-3 z-10 rounded-full bg-white/70 px-2 py-0.5 font-accent text-[10px] uppercase text-brand-black backdrop-blur-sm">
            Before
          </span>
        </div>

        {/* Slider Divider & Draggable Handle */}
        <div
          className="absolute inset-y-0 z-20 pointer-events-none"
          style={{
            left: `${percent}%`,
            transform: "translateX(-50%)",
            transition: dragging ? "none" : "left 0.7s cubic-bezier(0.22,1,0.36,1)",
          }}
        >
          <div className="mx-auto h-full w-[3px] bg-white shadow-[0_0_8px_rgba(0,0,0,0.3)]" />
          <div
            onPointerDown={handleHandlePointerDown}
            className="pointer-events-auto absolute left-1/2 top-1/2 flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-brand-orange-deep shadow-md ring-1 ring-brand-black/10 transition-transform duration-200 hover:scale-110 active:scale-95 touch-none cursor-ew-resize"
          >
            <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4">
              <path
                d="M8 7 4 12l4 5M16 7l4 5-4 5"
                stroke="currentColor"
                strokeWidth="2.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
      </div>
      <p className="mt-4 sm:mt-5 text-center text-sm font-semibold uppercase text-brand-black">{name}</p>
      <p className="mt-0.5 max-w-[12rem] text-center text-xs leading-snug text-brand-gray">{title}</p>
    </motion.div>
  );
}
